import type {
  FmMapFeatureDescriptor,
  FmMapSelection,
  FmMapWorkspaceDescriptor,
} from './FmMapWorkspaceRoot.types';
import {
  requireMapWorkspaceTextArray,
} from './FmMapWorkspaceValidation.shared';

function domainFeature(
  features: readonly FmMapFeatureDescriptor[],
  featureId: string,
  expectedKind?: 'stand' | 'plot',
  featurePropertyId?: string,
): FmMapFeatureDescriptor | undefined {
  const exact = features.find((candidate) => (
    (!expectedKind || candidate.kind === expectedKind)
    && candidate.id === featureId
  ));
  if (exact) return exact;
  const matches = features.filter((candidate) => {
    if (expectedKind === 'stand') {
      return candidate.kind === expectedKind
        && candidate.standId === featureId
        && (!featurePropertyId || candidate.propertyId === featurePropertyId);
    }
    return expectedKind === 'plot'
      && candidate.kind === expectedKind
      && candidate.plotId === featureId;
  });
  return matches.length === 1 ? matches[0] : undefined;
}

export function selectionForMapFeature(
  featureId: string,
  features: readonly FmMapFeatureDescriptor[],
  propertyId: string,
  expectedKind?: 'stand' | 'plot',
  featurePropertyId?: string,
): FmMapSelection {
  const feature = domainFeature(
    features,
    featureId,
    expectedKind,
    featurePropertyId,
  );
  if (!feature) return { kind: 'none' };
  const base = {
    id: feature.id,
    label: feature.label,
    geometryRef: feature.geometryRef,
  };
  if (feature.kind === 'property') {
    return { ...base, kind: feature.kind, propertyId: feature.propertyId! };
  }
  if (feature.kind === 'stand') {
    return {
      ...base,
      kind: feature.kind,
      propertyId: feature.propertyId!,
      standId: feature.standId!,
    };
  }
  if (feature.kind === 'campaign') {
    return {
      ...base,
      kind: feature.kind,
      campaignId: feature.campaignId!,
      campaignIds: feature.relatedCampaignIds ?? [feature.campaignId!],
      propertyIds: feature.relatedPropertyIds ?? [],
      plotIds: feature.relatedPlotIds ?? [],
    };
  }
  if (feature.kind === 'plot') {
    return {
      ...base,
      kind: feature.kind,
      propertyId: feature.propertyId!,
      campaignId: feature.campaignId!,
      plotId: feature.plotId!,
      standId: feature.standId ?? null,
    };
  }
  return {
    ...base,
    kind: 'risk',
    propertyId,
    riskId: feature.id,
    severity: 'high',
  };
}

export function actionDismissesMapPanel(
  descriptor: FmMapWorkspaceDescriptor,
  actionId: string,
): boolean {
  return descriptor.panels.some((panel) => {
    if (panel.kind !== 'collection') return false;
    if (panel.primaryAction?.id === actionId) {
      return panel.primaryAction.dismissesPanel === true;
    }
    if (panel.actions.some((action) =>
      action.id === actionId && action.dismissesPanel === true
    )) {
      return true;
    }
    return panel.records.some((record) =>
      record.actions.some((action) =>
        action.dismissesPanel === true
        && actionId === `${action.id}:${record.id}`
      )
    );
  });
}

function selectedFeature(
  descriptor: FmMapWorkspaceDescriptor,
  selection: Exclude<FmMapSelection, { kind: 'none' }>,
): FmMapFeatureDescriptor {
  const feature = descriptor.features.find(({ id }) => id === selection.id);
  if (
    !feature
    || feature.kind !== selection.kind
    || feature.geometryRef !== selection.geometryRef
    || feature.label !== selection.label
  ) {
    throw new Error(
      'Invalid map workspace descriptor: selection must reference one typed feature.',
    );
  }
  return feature;
}

function validateCampaignSelection(
  selection: Extract<FmMapSelection, { kind: 'campaign' }>,
  feature: FmMapFeatureDescriptor,
): void {
  const identityMatches = selection.campaignId === feature.campaignId
    || feature.relatedCampaignIds?.includes(selection.campaignId);
  if (!identityMatches) {
    throw new Error(
      'Invalid map workspace descriptor: campaign selection identity must match its feature.',
    );
  }
  requireMapWorkspaceTextArray(selection.campaignIds, 'selection.campaignIds');
  requireMapWorkspaceTextArray(selection.propertyIds, 'selection.propertyIds');
  if (selection.plotIds.length > 0) {
    requireMapWorkspaceTextArray(selection.plotIds, 'selection.plotIds');
  }
  if (!selection.campaignIds.includes(selection.campaignId)) {
    throw new Error(
      'Invalid map workspace descriptor: campaign selection must include its active partition.',
    );
  }
}

function validateDomainIdentity(
  selection: Exclude<FmMapSelection, { kind: 'none' | 'campaign' }>,
  feature: FmMapFeatureDescriptor,
): void {
  if (
    selection.kind === 'property'
    && selection.propertyId !== feature.propertyId
  ) {
    throw new Error(
      'Invalid map workspace descriptor: property selection identity must match its feature.',
    );
  }
  if (
    selection.kind === 'stand'
    && (
      selection.standId !== feature.standId
      || selection.propertyId !== feature.propertyId
    )
  ) {
    throw new Error(
      'Invalid map workspace descriptor: stand selection identity must match its feature.',
    );
  }
  if (selection.kind === 'plot' && selection.plotId !== feature.plotId) {
    throw new Error(
      'Invalid map workspace descriptor: plot selection identity must match its feature.',
    );
  }
  if (selection.kind === 'risk' && selection.riskId !== feature.id) {
    throw new Error(
      'Invalid map workspace descriptor: risk selection identity must match its feature.',
    );
  }
}

export function validateMapWorkspaceSelection(
  descriptor: FmMapWorkspaceDescriptor,
): void {
  if (descriptor.selection.kind === 'none') return;
  const feature = selectedFeature(descriptor, descriptor.selection);
  if (descriptor.selection.kind === 'campaign') {
    validateCampaignSelection(descriptor.selection, feature);
    return;
  }
  validateDomainIdentity(descriptor.selection, feature);
}
